/**
 * End-of-session celebration. Spec §4.6.
 *
 * Four beats Eva paces through herself (only confetti auto-completes):
 *
 *   1. **confetti + score** — confetti, Pumpkin in cheer pose, score
 *      framed positively ("You got 7 out of 10 today!"). Even on 0/10
 *      the framing is for *practising*, not winning.
 *
 *   2. **new stickers** — each freshly unlocked sticker reveals with a
 *      "new!" tag. Tap to advance through each. If nothing new
 *      unlocked, this beat is skipped silently.
 *
 *   3. **Pumpkin praise** — short personalised line picked from a
 *      handful of scripted variations to keep it from going stale.
 *
 *   4. **sign-off** — "See you tomorrow!" button → home.
 *
 * Lifecycle:
 *   - On mount we POST the session to /api/sessions (the only place
 *     that persists). The response includes any newly-unlocked
 *     stickers; we use them for beat 2.
 *   - If the POST fails (network blip), we still let Eva see the
 *     celebration — losing the persistence is unfortunate but
 *     forcing her to look at an error screen would feel like
 *     punishment for finishing. A toast/banner shows on the sign-off
 *     beat. Persistence retry isn't in scope for v1.
 *   - When she taps the final sign-off, we POST /api/achievements/seen
 *     to clear the new! badges, clearSession() the in-memory session,
 *     and navigate home.
 *
 * Pumpkin returns here (spec §7: "Visible on... Celebration"). Pose
 * is cheer-celebrate from arrival through praise; sign-off uses
 * curled-rest as the "see you tomorrow" cue.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Button } from "@/components/Button";
import { PumpkinFigure } from "@/components/PumpkinFigure";
import { StickerEmblem } from "@/components/StickerEmblem";
import { useNavigation } from "@/context/NavigationContext";
import { useSession } from "@/context/SessionContext";
import { api } from "@/lib/api";
import type { UnlockedSticker } from "@/types/data";
import { buildSessionPayload } from "@/lib/sessionPayload";
import { playCelebration, playStickerReveal } from "@/lib/sounds";
import { missedFromFirstPass, attemptMatches } from "@/lib/spellingCompare";
import { cancelSpeech, speak } from "@/lib/speech";
import "./CelebrationScreen.css";

const PRAISE_LINES = [
  "Great job practising today, Eva!",
  "I'm so proud of you for finishing!",
  "You worked really hard — well done!",
  "Look at you, doing your practice!",
  "Brilliant practising today!",
];

type Beat = "score" | "stickers" | "praise" | "signoff";

interface PersistState {
  kind: "pending" | "ok" | "failed";
  newlyUnlocked: UnlockedSticker[];
}

export function CelebrationScreen() {
  const { navigate } = useNavigation();
  const { session, clearSession } = useSession();
  const [beat, setBeat] = useState<Beat>("score");
  const [stickerIndex, setStickerIndex] = useState(0);
  const [persist, setPersist] = useState<PersistState>({
    kind: "pending",
    newlyUnlocked: [],
  });
  // Stable across re-renders: the start time we record into the session
  // row. Using session-mount as a proxy for session-end is fine; the
  // exact value matters far less than that one exists.
  const completedAtRef = useRef(new Date().toISOString());
  const persistStartedRef = useRef(false);

  // Bounce home if there's no session — defensive.
  useEffect(() => {
    if (session === null || session.words.length === 0) {
      navigate("home");
    }
  }, [session, navigate]);

  // Score = number of first-pass-correct answers.
  const score = useMemo(() => {
    if (!session) return 0;
    return session.words.reduce(
      (n, w) =>
        n + (attemptMatches(session.firstPassAttempts[w.id] ?? "", w.spelling) ? 1 : 0),
      0,
    );
  }, [session]);
  const total = session?.words.length ?? 0;

  // Pumpkin praise line: pick once per mount so it doesn't change as she taps.
  const praiseLine = useMemo(
    () => PRAISE_LINES[Math.floor(Math.random() * PRAISE_LINES.length)],
    [],
  );

  // ─── Persist session on mount ───────────────────────────────────
  useEffect(() => {
    if (!session) return;
    if (persistStartedRef.current) return;
    persistStartedRef.current = true;

    (async () => {
      const completedAt = completedAtRef.current;
      // session.startedAt was stamped by FlashCardScreen via startSession;
      // it's the start of this whole gameplay loop.
      const startedAt = session.startedAt;
      const timeSpentSeconds = Math.max(
        0,
        Math.round(
          (new Date(completedAt).getTime() - new Date(startedAt).getTime()) / 1000,
        ),
      );
      const payload = buildSessionPayload(session, {
        startedAt,
        completedAt,
        timeSpentSeconds,
        // Spec §5.4: past-week reviews persist as sessions but the
        // session row's counts_toward_progress = 0, so the engine
        // (streak, mastery, achievement unlocks) skips them.
        countsTowardProgress: session.countsTowardProgress,
      });
      const res = await api.createSession(payload);
      if (res.status === 201 && res.body && "session" in res.body) {
        setPersist({ kind: "ok", newlyUnlocked: res.body.newlyUnlocked });
      } else {
        setPersist({ kind: "failed", newlyUnlocked: [] });
      }
    })();
  }, [session]);

  // ─── Speak the score line on beat 1 ─────────────────────────────
  useEffect(() => {
    if (!session) return;
    if (beat !== "score") return;
    // Whoosh + warm chime under the confetti (design.md §8).
    void playCelebration();
    speak(`You got ${score} out of ${total} today!`, session.settings);
    return () => cancelSpeech();
  }, [beat, session, score, total]);

  // ─── Speak the praise line on beat 3 ────────────────────────────
  useEffect(() => {
    if (!session) return;
    if (beat !== "praise") return;
    speak(praiseLine, session.settings);
    return () => cancelSpeech();
  }, [beat, session, praiseLine]);

  // Beat advancement (also handles skipping beat 2 when no new stickers).
  const advance = useCallback(() => {
    setBeat((b) => {
      if (b === "score") {
        return persist.newlyUnlocked.length > 0 ? "stickers" : "praise";
      }
      if (b === "stickers") {
        const next = stickerIndex + 1;
        if (next < persist.newlyUnlocked.length) {
          setStickerIndex(next);
          return "stickers";
        }
        return "praise";
      }
      if (b === "praise") return "signoff";
      return b;
    });
  }, [persist.newlyUnlocked.length, stickerIndex]);

  const handleSignOff = useCallback(async () => {
    cancelSpeech();
    // Mark seen the stickers she just viewed. Fire-and-forget — Eva
    // doesn't wait on the network here.
    if (persist.newlyUnlocked.length > 0) {
      const oneTimeIds = persist.newlyUnlocked
        .filter((s) => s.category !== "per_session" && s.category !== "alphabet")
        .map((s) => s.id);
      const perSessionRowIds = persist.newlyUnlocked
        .filter((s) => s.perSessionRowId !== undefined)
        .map((s) => s.perSessionRowId as number);
      // Alphabet ids look like "letter_a" — the seen endpoint wants the
      // bare letter.
      const alphabetLetters = persist.newlyUnlocked
        .filter((s) => s.category === "alphabet")
        .map((s) => s.id.replace(/^letter_/, ""));
      void api.markAchievementsSeen({ oneTimeIds, perSessionRowIds, alphabetLetters });
    }
    clearSession();
    navigate("home");
  }, [persist.newlyUnlocked, clearSession, navigate]);

  if (!session) return null;

  // Defensive: include missed-word count for the score copy framing.
  const missed = missedFromFirstPass(session.words, session.firstPassAttempts).length;
  void missed;

  return (
    <main className="celebration">
      {beat === "score" && (
        <div className="celebration__inner celebration__inner--score">
          <Confetti />
          <PumpkinFigure
            className="celebration__pumpkin pumpkin-idle"
            pose="cheerCelebrate"
            alt="Pumpkin the pangolin, cheering"
          />
          <h1 className="celebration__title">
            You got <span className="celebration__score">{score}</span> out of {total} today!
          </h1>
          <p className="celebration__sub">Great practising, Eva.</p>
          <Button size="large" onClick={advance}>
            See what&rsquo;s next
          </Button>
        </div>
      )}

      {beat === "stickers" && persist.newlyUnlocked[stickerIndex] && (
        <StickerReveal
          sticker={persist.newlyUnlocked[stickerIndex]}
          stepLabel={`Sticker ${stickerIndex + 1} of ${persist.newlyUnlocked.length}`}
          onNext={advance}
        />
      )}

      {beat === "praise" && (
        <div className="celebration__inner celebration__inner--praise">
          <PumpkinFigure
            className="celebration__pumpkin pumpkin-idle"
            pose="cheerCelebrate"
            alt="Pumpkin the pangolin, cheering"
          />
          <p className="celebration__praise">{praiseLine}</p>
          <Button size="large" onClick={advance}>
            Thank you!
          </Button>
        </div>
      )}

      {beat === "signoff" && (
        <div className="celebration__inner celebration__inner--signoff">
          <PumpkinFigure
            className="celebration__pumpkin"
            pose="curledRest"
            alt="Pumpkin the pangolin, curled up resting"
          />
          <h2 className="celebration__signoff-title">See you tomorrow!</h2>
          {persist.kind === "failed" && (
            <p className="celebration__warn">
              (We had trouble saving today&rsquo;s session, but Pumpkin still
              saw all your practice!)
            </p>
          )}
          <Button size="large" onClick={handleSignOff}>
            Goodnight!
          </Button>
        </div>
      )}
    </main>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────

function StickerReveal({
  sticker,
  stepLabel,
  onNext,
}: {
  sticker: UnlockedSticker;
  stepLabel: string;
  onNext: () => void;
}) {
  // Soft chime + paper-place per design.md §8. Keyed on the sticker id
  // so each new reveal in the queue plays the cue once on mount, even
  // when StickerReveal is re-rendered with the same component identity.
  useEffect(() => {
    void playStickerReveal();
  }, [sticker.id]);

  return (
    <div className="celebration__inner celebration__inner--sticker">
      <p className="celebration__sticker-step">{stepLabel}</p>
      <div className="celebration__sticker-host">
        <StickerEmblem
          stickerId={sticker.id}
          category={sticker.category}
          name={sticker.name}
          isNew
        />
      </div>
      <Button size="large" onClick={onNext}>
        Cool!
      </Button>
    </div>
  );
}

/**
 * Lightweight CSS confetti — geometric warm-palette shapes per design.md §7
 * (bouncy spring, 1500–2000ms). Hand-rolled rather than a library to keep
 * bundle size honest; we can swap in something fancier in Phase 8.
 */
function Confetti() {
  const pieces = useMemo(
    () =>
      Array.from({ length: 28 }, (_, i) => ({
        id: i,
        leftPct: Math.random() * 100,
        delayMs: Math.random() * 300,
        durationMs: 1600 + Math.random() * 600,
        rotateDeg: Math.random() * 360,
        size: 10 + Math.random() * 10,
        kind: i % 3, // 0=square 1=circle 2=triangle (via clip-path)
      })),
    [],
  );
  return (
    <div className="confetti" aria-hidden="true">
      {pieces.map((p) => (
        <span
          key={p.id}
          className={`confetti__piece confetti__piece--k${p.kind}`}
          style={{
            left: `${p.leftPct}%`,
            width: `${p.size}px`,
            height: `${p.size}px`,
            animationDelay: `${p.delayMs}ms`,
            animationDuration: `${p.durationMs}ms`,
            transform: `rotate(${p.rotateDeg}deg)`,
          }}
        />
      ))}
    </div>
  );
}
